import { useEffect, useRef, useState } from "react";
import { FaBars, FaTimes, FaBell, FaRegBell, FaUser } from "react-icons/fa";
import { VscSignOut } from "react-icons/vsc";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import NeedAuthModal from "../Modals/NeedAuthModal";
import GuestBookingModal from "../Modals/GuestInformationModal";
import { useGetUserData } from "../../hooks/useGetUserData";
import { getImg } from "../../Api/getImg";
import { FaTimesCircle, FaCheckCircle, FaHistory } from "react-icons/fa";
import {
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  ClockCircleFilled,
  ClockCircleOutlined,
  CameraOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import {
  useMarkAllAsRead,
  useMarkAsRead,
  useNotifications,
  useUnreadCount,
  useOpenNotification,
} from "../../hooks/useNotficationQuery";
import { useQueryClient } from "@tanstack/react-query";
import popupSound from "../../assets/notficationSound.mp3";
import { useDoctorQuery } from "../../hooks/useDoctorQuery";
import { NetworkStatusAlert } from "../NetworkBadge/NetworkBadge";
import { useDoctorContext } from "../../context/DoctorContext";
import { GrSchedule } from "react-icons/gr";
import Badge from "../../Components/Dropdown/Badge";
import Dropdown from "../../Components/Dropdown/Dropdown";
import Menu, { MenuItem } from "../../Components/Dropdown/MenuDropDown";
import { IoIosPaper } from "react-icons/io";
import LanguageSwitcher from "../Ui/LanguageSwitcher";
import { useLanguage } from "../../context/LanguageContext";
import { RiMenu3Line, RiMenu4Fill, RiUserFill } from "react-icons/ri";
import { slug } from "../../utils/store";
import { BsHospital } from "react-icons/bs";

const Header = ({
  user,
  loginModal,
  setLoginModal,
  registerModal,
  setRegisterModal,
}) => {
  const { t, isRTL } = useLanguage();
  const { hasClinics, hasServices, hasBlogs, hasOffers,hasReviews,hasDoctors, hasNews } = useDoctorContext();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isGuestModalVisible, setIsGuestModalVisible] = useState(false);
  const [pendingRoute, setPendingRoute] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { mutate: markAllAsRead } = useMarkAllAsRead();
  const { mutate: markAsRead } = useMarkAsRead();
  const { mutate: openNotification } = useOpenNotification();
const{data:doctorData}=useDoctorQuery(slug)
  const queryClient = useQueryClient();
  const { data: notifications = [], isLoading } = useNotifications();
  const { data: unreadCount } = useUnreadCount();

  const previousNotificationCountRef = useRef(notifications.length);
  const previousUnreadCountRef = useRef(unreadCount);
  const audioRef = useRef(null);
  const observerRef = useRef(null);

 const navLinks = [
  { label: t("header.nav.about"), id: "about" },
  hasClinics && { label: t("header.nav.clinics"), id: "doctor" },
  hasDoctors && { label: t("header.nav.team"), id: "team" },
  hasServices && { label: t("header.nav.services"), id: "services" },
  hasNews && { label: t("header.nav.news"), id: "news" },
  hasBlogs && { label: t("header.nav.blogs"), id: "blogs" },
  hasOffers && { label: t("header.nav.packages"), id: "packages" },
  hasReviews && { label: t("header.nav.reviews"), id: "reviews" },
  { label: t("header.nav.contact"), id: "contact" },
].filter(Boolean);

  const isAuthorized =
    !!localStorage.getItem(`userTkn-${slug}`) ||
    !!sessionStorage.getItem(`userTkn-${slug}`);

  const isHomePage = () => {
    const homePath = `/`;
    return (
      location.pathname === homePath ||
      location.pathname === `/` ||
      location.pathname === `/home`
    );
  };

  const isNotificationSupported = () => {
    return typeof window !== "undefined" && "Notification" in window;
  };

  const requestNotificationPermission = async () => {
    if (!isNotificationSupported()) {
      return "denied";
    }

    try {
      return await Notification.requestPermission();
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      return "denied";
    }
  };

  const showNotification = (title, options) => {
    if (!isNotificationSupported()) {
      return null;
    }

    try {
      if (Notification.permission === "granted") {
        return new Notification(title, options);
      }
    } catch (error) {
    }
    return null;
  };

  const handleLogout = () => {
    localStorage.removeItem(`userTkn-${slug}`);
    sessionStorage.removeItem(`userTkn-${slug}`);
    // navigate(`/${slug}/home`);
    window.location.href = `/home`;
  };

  const handleProtectedNavigation = (route) => {
    if (!isAuthorized) {
      setPendingRoute(route);
      setIsModalVisible(true);
    } else {
      navigate(route);
      setIsMobileMenuOpen(false);
    }
  };

 
  const setupIntersectionObserver = () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (!isHomePage()) {
      setActiveSection(null);
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        let mostVisibleSection = null;
        let maxIntersectionRatio = 0;

        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            entry.intersectionRatio > maxIntersectionRatio
          ) {
            maxIntersectionRatio = entry.intersectionRatio;
            mostVisibleSection = entry.target.id;
          }
        });

        if (mostVisibleSection) {
          setActiveSection(mostVisibleSection);
        }
      },
      {
        rootMargin: "-20% 0px -20% 0px",
        threshold: [0.1, 0.3, 0.5, 0.7, 0.9],
      },
    );

    const timer = setTimeout(() => {
      navLinks.forEach((link) => {
        const el = document.getElementById(link.id);
        if (el && observerRef.current) {
          observerRef.current.observe(el);
        }
      });
    }, 500);

    return () => clearTimeout(timer);
  };

  const handleNotificationClick = (notification) => {

    openNotification(notification.id);

    if (notification.type === "reminder") {
      navigate(`/schedule-reminder`);
    } else if (notification.type == "BlogCreated") {
      navigate(`/blog/${notification.entityId}`);
    } else {
      navigate(`/my-appointments`);
    }
  };

  useEffect(() => {
    if (audioRef.current === null) {
      audioRef.current = new Audio(popupSound);
    }

    if (isNotificationSupported() && Notification.permission === "default") {
      requestNotificationPermission();
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        audioRef.current.load();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    if (
      unreadCount !== undefined &&
      previousUnreadCountRef.current !== undefined &&
      unreadCount > previousUnreadCountRef.current
    ) {


      try {
        audioRef.current.play().catch(() => {});
      } catch {}

      if (
        document.hidden &&
        isNotificationSupported() &&
        notifications.length > 0
      ) {
        const latest = notifications[0];
        const notification = showNotification(latest.title, {
          body: latest.body,
          icon: "/logo.png",
        });

        if (notification) {
          notification.onclick = () => {
            window.focus();
            notification.close();
          };
        }
      }
    }

    previousUnreadCountRef.current = unreadCount;

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [unreadCount, notifications]);

  useEffect(() => {
    if (!isHomePage()) {
      setActiveSection(null);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      return;
    }

    const timer = setTimeout(() => {
      setupIntersectionObserver();
    }, 300);

    return () => {
      clearTimeout(timer);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [location.pathname, slug, hasServices, hasOffers, hasBlogs, hasClinics]);

  const userMenuItems = [
    MenuItem({
      key: "profile",
      icon: <UserOutlined />,
      label: t("header.menu.profile"),
      href: `/user-profile`,
    }),
    MenuItem({
      key: "reports",
      icon: <IoIosPaper />,
      label: t("header.menu.reports"),
      href: `/my-reports`,
    }),
    MenuItem({
      key: "scheduel",
      icon: <ClockCircleOutlined />,
      label: t("header.menu.schedule"),
      href: `/schedule-reminder`,
    }),
    MenuItem({
      key: "settings",
      icon: <SettingOutlined />,
      label: t("header.menu.settings"),
      href: `/settings`,
    }),
    ...(isAuthorized
      ? [
          MenuItem({
            key: "logout",
            icon: <LogoutOutlined className="text-red-600" />,
            label: t("header.menu.logout"),
            onClick: handleLogout,
            className: "text-red-600 hover:bg-red-50",
          }),
        ]
      : []),
  ];

  const scrollToSection = async (id) => {
    const currentPath = window.location.pathname;
    const homePath = ``;

    if (currentPath !== homePath && currentPath !== `${homePath}/home`) {
      navigate(homePath);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const offset = window.innerWidth > 768 ? 100 : 250;
          const y = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
        setTimeout(() => {
          setupIntersectionObserver();
          setActiveSection(id);
        }, 800);
      }, 200);
    } else {
      const el = document.getElementById(id);
      if (el) {
        const offset = window.innerWidth > 768 ? 100 : 250;
        const y = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: y, behavior: "smooth" });
        setActiveSection(id);
      }
    }

    setIsMobileMenuOpen(false);
  };

  const getNotificationIcon = (type) => {

    switch (type) {
      case "BookCanceled":
        return <FaTimesCircle className="text-red-500 text-xl" />;
      case "BookCompleted":
        return <FaCheckCircle className="text-green-500 text-xl" />;
      case "BookSkipped":
        return <FaHistory className="text-yellow-500 text-xl" />;
      case "BlogCreated":
        return <CameraOutlined className="text-yellow-500 text-xl" />;
      case "BookingReminder":
        return <ClockCircleOutlined className="text-secondary text-xl" />;
      default:
        return <FaBell className="text-secondary text-xl" />;
    }
  };

  const notificationsDropdown = (
    <div className="bg-white rounded-lg shadow-xl w-64 md:w-96 mt-1  border">
      <div
        className={`absolute top-[1px] md:top-[1px]  ${isRTL ? "left-1 md:left-2" : "right-1 md:right-2"} w-3 h-3 bg-primary transform -rotate-45  border-r border-b border-primary`}
      ></div>

      <div className="border-b rounded-t-md px-5 py-3 flex justify-between items-center bg-primary">
        <h3 className="text-xs md:text-sm font-semibold text-white">
          {t("header.notifications.title")}
        </h3>
        <div className="space-x-2 text-xs md:text-sm text-secondary font-medium">
          <button
            onClick={() => {
              markAllAsRead();
            }}
            className="text-white hover:text-gray-200 transition-colors"
          >
            {t("header.notifications.markAllRead")}
          </button>
        </div>
      </div>

      <div className="p-3 px-2 md:px-5 max-h-[350px] overflow-y-auto">
        <p className="text-xs md:text-sm text-gray-500 mb-2">
          {t("header.notifications.earlier")}
        </p>
        {notifications.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-4">
            {t("header.notifications.noNotifications")}
          </p>
        ) : (
          notifications.map((n) => (
            <div
              key={n.id}
              className={`flex gap-3 items-start py-2 border-b last:border-none px-1 cursor-pointer rounded transition my-1
                                ${!n.isOpened ? "bg-blue-100 border-blue-200" : "bg-white"} hover:bg-gray-50`}
              onClick={() => handleNotificationClick(n)}
            >
              <div className="mt-1 rounded-full flex items-center justify-center text-gray-600">
                {getNotificationIcon(n.type)}
              </div>
              <div className="flex-1">
                <p
                  className={`text-xs md:text-sm text-gray-800 ${!n.isOpened ? "font-semibold" : ""}`}
                >
                  {n.title}
                </p>
                <p className="text-xs text-gray-600">{n.body}</p>
                <p className="text-[10px] text-gray-400">
                  {new Date(n.createdAt).toLocaleString()}
                </p>
              </div>
              {!n.isOpened && (
                <span className="w-2 h-2 bg-blue-500 rounded-full mt-1"></span>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <header
      dir={isRTL ? "rtl" : "ltr"}
      className="bg-[#fcfbf9] border-b border-gray-200 py-2 md:py-3 sticky top-0 z-40 "
    >
      <div className="px-4 xl:px-32  flex justify-between items-center">
        <div className="flex justify-between items-center w-full md:hidden">
          <div className="flex items-center justify-center gap-2">
             <a
            href={`/home`}
            className=" md:hidden items-center space-x-4"
          >
            {doctorData?.logoId?
            <img src={getImg(doctorData?.logoId)} className=" w-20 " />:<BsHospital className=" text-secondary"/>}
          </a>
              {/* <a
            href={`/home`}
            className=" md:hidden items-center space-x-4"
          >
            <img src="/images/headerLogo.png" className=" w-20 " />
          </a> */}
            <LanguageSwitcher />
          </div>
          <div className="flex items-center gap-4 ">
            {isAuthorized ? (
              <div className=" flex items-center gap-3 space-x- md:space-x-6">
                {/*  Notification */}
                <Dropdown
                  overlay={notificationsDropdown}
                  trigger={["click"]}
                  placement="notDrop"
                  onOpenChange={(visible) => {
                    if (visible) {
                      markAllAsRead();
                      queryClient.invalidateQueries(["notifications", slug]);
                      queryClient.invalidateQueries(["unreadCount", slug]);
                    }
                  }}
                >
                  <Badge count={unreadCount} offset={[0, 1]}>
                    <FaRegBell className="text-lg mx-3 md:text-2xl text-gray-700 cursor-pointer " />
                  </Badge>
                </Dropdown>

                {/*  Avatar */}
                <Dropdown
                  overlay={<Menu items={userMenuItems} isRTL={isRTL} />}
                  trigger={["click"]}
                  placement={isRTL ? "bottomRight" : "bottomLeft"}
                >
                  <div className="cursor-pointer flex flex-col items-center">
                    <img
                      className="w-7 h-7  md:w-12 md:h-12 object-cover rounded-full "
                      src={
                        user?.imageId
                          ? getImg(user?.imageId)
                          : "/anonymous.png"
                      }
                      alt="User"
                    />
                  </div>
                </Dropdown>
              </div>
            ) : (
              <div className="md:flex items-center space-x-6">
                <div
                  onClick={() => setLoginModal(true)}
                  className="cursor-pointer w-8 h-8 rounded-full border-2 border-secondary_hover flex items-center justify-center text-primary  hover:text-primary transition duration-200"
                >
                  <UserOutlined style={{ fontSize: 16 }} />
                </div>
              </div>
            )}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              {isMobileMenuOpen ? <RiMenu4Fill className="text-secondary_hover" size={18} /> : <RiMenu3Line className="text-secondary_hover" size={18} />}
            </button>
          </div>
        </div>
        <div className="flex  gap-2 items-center">
          <a
            href={`/home`}
            className="hidden md:flex items-center space-x-4"
          >
    {doctorData?.logoId?
            <img src={getImg(doctorData?.logoId)} className=" w-36 " />:<BsHospital className="w-10 h-10 text-secondary"/>}    
            
                  </a>
                    {/* <a
            href={`/home`}
            className="hidden md:flex items-center space-x-4"
          >
            <img src="/images/headerLogo.png" className=" w-36 " />
          </a> */}
          <span
            className="hidden md:block
          "
          ></span>
        </div>
        <nav
          dir={isRTL ? "rtl" : "ltr"}
          className="hidden md:flex items-center xl:gap-4 gap-2 text-gray-600 font-medium text-sm"
        >
          {navLinks.map((link) =>
            link.id != "contact" ? (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className={`relative px-2 py-1 text-base transition-colors duration-200
                                ${
                                  activeSection === link.id && isHomePage()
                                    ? "goldy-animate font-semibold"
                                    : "text-gray-600"
                                } group`}
              >
                {link.label}
                <span
                  className={`absolute left-0 -bottom-0.5 h-0.5 bg-gold transition-all duration-300
                                    ${activeSection === link.id && isHomePage() ? "w-full" : "w-0 group-hover:w-full"}
                                `}
                ></span>
                {/* {link.id == "news" && (
                  <span
                    className={`absolute animate-pulse -top-1  ${isRTL ? "left-12" : "left-[94px]"} w-7 h-7 border border-t-0 border-b-0 border-l-0  rounded-full px-3 border-r-1 border-r-secondary -rotate-45   "`}
                  >
                    <span className="absolute top-1 border-t-0 border-b-0 border-l-0  border-r-1 right-1 w-5 h-5 border rounded-full px-3 border-r-1 border-r-secondary  ">
                      <span className="absolute top-1 rotate-45 right-1 bg-gold  w-3 h-3 animate-none text-xs flex items-center justify-center rounded-full text-white"></span>
                    </span>
                  </span>
                )} */}
              </button>
            ) : (
              <button
                key={link.id}
                onClick={() => navigate(`/contact-us`)}
                className={`relative px-2 py-1 text-base transition-colors duration-200
                                ${
                                  activeSection === link.id && isHomePage()
                                    ? " goldy font-semibold"
                                    : "text-gray-600"
                                } group`}
              >
                {link.label}
                <span
                  className={`absolute left-0 -bottom-0.5 h-0.5 bg-gold transition-all duration-300
                                    ${activeSection === link.id && isHomePage() ? "w-full" : "w-0 group-hover:w-full"}
                                `}
                ></span>
                {link.id == "news" && (
                  <span
                    className={`absolute animate-pulse -top-1  ${isRTL ? "left-12" : "left-[94px]"} w-7 h-7 border border-t-0 border-b-0 border-l-0  rounded-full px-3 border-r-1 border-r-secondary -rotate-45   "`}
                  >
                    <span className="absolute top-1 border-t-0 border-b-0 border-l-0  border-r-1 right-1 w-5 h-5 border rounded-full px-3 border-r-1 border-r-secondary  ">
                      <span className="absolute top-1 rotate-45 right-1 bg-secondary  w-3 h-3 animate-none text-xs flex items-center justify-center rounded-full text-white"></span>
                    </span>
                  </span>
                )}
              </button>
            ),
          )}
        </nav>
        <div className="flex items-center gap-7">
          <span className="hidden md:block">
            <LanguageSwitcher />
          </span> 

          {isAuthorized ? (
            <div className="hidden md:flex items-center space-x-6">
              {/*  Notification */}
              <Dropdown
                overlay={notificationsDropdown}
                trigger={["click"]}
                placement={"notDrop"}
                onOpenChange={(visible) => {
                  if (visible) {
                    markAllAsRead();
                    queryClient.invalidateQueries(["notifications", slug]);
                    queryClient.invalidateQueries(["unreadCount", slug]);
                  }
                }}
              >
                <Badge count={unreadCount} offset={[0, 5]}>
                  <FaRegBell className="text-2xl   text-gray-700 cursor-pointer" />
                </Badge>
              </Dropdown>

              {/*  Avatar */}
              <Dropdown
                overlay={<Menu items={userMenuItems} />}
                trigger={["click"]}
                placement={isRTL ? "bottomRight" : "bottomLeft"}
              >
                <div className="cursor-pointer  mx-3 flex flex-col items-center">
                  <img
                    className="w-12 h-12 mx-5 mr-7 object-cover rounded-full "
                    src={
                      user?.imageId
                        ? getImg(user?.imageId)
                        :  "/anonymous.png"
                    }
                    alt="User"
                  />
                  <p className="text-sm font-medium text-gray-700">
                    {user?.firstName || t("header.menu.profile")}
                  </p>
                </div>
              </Dropdown>
            </div>
          ) : (
            <div className="hidden md:flex items-center space-x-6">
              <div
                onClick={() => setLoginModal(true)}
                className="cursor-pointer flex flex-col items-center gap-1"
              >
                <div className="w-10 h-10 rounded-full border-2 border-secondary_hover flex items-center justify-center text-primary  hover:text-white hover:bg-primary transition duration-200">
                  <FaUser style={{ fontSize: 18 }} />
                </div>
                {/* <p className="text-xs text-gray-500">{t("header.auth.login")}</p> */}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* address
 Nav */}
      {isMobileMenuOpen && (
        <div className="md:hidden mt-4 space-y-3 px-5">
          <nav className="flex flex-col space-y-2 text-gray-700 font-medium text-sm">
            {navLinks.map((link) => (
                          link.id != "contact" ? (

              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className={`relative text-left ${
                  activeSection === link.id && isHomePage()
                    ? "goldy-animate font-semibold"
                    : "text-gray-700"
                } hover:text-[#f59e0b] transition`}
              >
                {link.label}
                {link.id == "news" && (
                  <span
                    className={`absolute -top-1 ${isRTL ? "right-[87%]" : "right-[80%]"} bg-goldy  w-4 h-4 animate-pulse text-[10px] flex items-center justify-center rounded-full text-white`}
                  >
                    {4}
                  </span>
                )}
              </button>):
               <button
                key={link.id}
                onClick={() => navigate(`/contact-us`)}
                className={`relative text-left ${
                  activeSection === link.id && isHomePage()
                    ? "text-[#f59e0b] font-semibold"
                    : "text-gray-700"
                } hover:text-[#f59e0b] transition`}
              >
                {link.label}
                {link.id == "news" && (
                  <span
                    className={`absolute -top-1 ${isRTL ? "right-[87%]" : "right-[80%]"} bg-goldy  w-4 h-4 animate-pulse text-[10px] flex items-center justify-center rounded-full text-white`}
                  >
                    {4}
                  </span>
                )}
              </button>
            ))}
          </nav>
{/* 
          <div>
            <button
              className="bg-secondary hover:bg-secondary_hover text-white w-full py-2 rounded-lg text-sm font-semibold transition mb-1"
              onClick={() =>
                handleProtectedNavigation(`/book-appointment`)
              }
            >
              {t("header.buttons.getTreatment")}
            </button>

            <button
              className="bg-primary hover:bg-primary_hover text-white w-full py-2 rounded-lg text-sm font-semibold transition"
              onClick={() =>
                handleProtectedNavigation(`/my-appointments`)
              }
            >
              {t("header.buttons.appointments")}
            </button>
          </div> */}
        </div>
      )}

      <NetworkStatusAlert />
    </header>
  );
};

export default Header;
